import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { IconGift, IconPlus, IconTrash } from "@tabler/icons-react";
import { Gift, Product } from "../../../types/types";
import {
  useMerchantAllListData,
  useMerchantListData,
  useAllFMMerchantData,
} from "../../../apis/merchantApis";
import { useProductListDataById } from "../../../apis/promoApis";
import DatePicker from "../../../components/DatePicker";
import PremiumDateRangePicker from "../../../components/PremiumDateRangePicker";
import { useLanguage } from "../../../context/LanguageContext";
import NewinputField from "../../../components/NewInput";
import NewSelectInput from "../../../components/NewSelectInput";

interface giftForm {
  merchant: string;
  merchantId?: string;
  giftSku: string;
  mainProductSku: string;
  mainQty: number;
  giftProductSku: string;
  giftQty: number;
  startDate: string;
  endDate: string;
  giftItems?: Array<{ sku: string; qty: number }>;
}

interface GiftsProps {
  products: Product[];
  gifts: Gift[];
  clients?: any[];
  giftForm: giftForm;
  onGiftFormChange: (updates: Partial<giftForm>) => void;
  onAddGift: (e: React.FormEvent) => void;
  onDeleteGift: (id: string) => void;
  onCancel: () => void;
  editingGiftId?: string;
  onEditGift?: (gift: Gift) => void;
}

export const Gifts: React.FC<GiftsProps> = ({
  products,
  gifts,
  clients,
  giftForm,
  onGiftFormChange,
  onAddGift,
  onDeleteGift,
  onCancel,
  editingGiftId,
  onEditGift,
}) => {
  const { t } = useLanguage();
  // Fetch Redux state
  const reduxClients = useSelector((state: any) => state?.client?.data);

  // Fetch merchant data directly from API as dynamic fallback
  const { data: apiClientsResponse } = useMerchantAllListData();
  const { data: listClientsResponse } = useMerchantListData(
    1,
    200,
    "",
    "MERCHANT",
  );
  const { data: fmClientsResponse } = useAllFMMerchantData();

  const selectedMerchantId = giftForm.merchantId;
  const { data: merchantProductsResponse } = useProductListDataById(
    selectedMerchantId,
    true,
  );

  // Helper to extract client list recursively from any payload structure
  const extractClients = (sourceData: any): any[] => {
    if (!sourceData) return [];
    if (Array.isArray(sourceData)) return sourceData;
    if (Array.isArray(sourceData?.data)) return sourceData.data;
    if (Array.isArray(sourceData?.clients)) return sourceData.clients;
    if (Array.isArray(sourceData?.data?.clients))
      return sourceData.data.clients;
    if (Array.isArray(sourceData?.lmClients)) return sourceData.lmClients;
    if (Array.isArray(sourceData?.data?.lmClients))
      return sourceData.data.lmClients;
    if (Array.isArray(sourceData?.merchants)) return sourceData.merchants;
    if (Array.isArray(sourceData?.data?.merchants))
      return sourceData.data.merchants;
    if (Array.isArray(sourceData?.items)) return sourceData.items;
    if (Array.isArray(sourceData?.data?.items)) return sourceData.data.items;

    if (typeof sourceData === "object") {
      for (const key of Object.keys(sourceData)) {
        if (Array.isArray(sourceData[key])) {
          return sourceData[key];
        }
      }
    }
    return [];
  };

  // Combine merchants with access FM or BOTH
  const filteredMerchants = useMemo(() => {
    const rawProps = extractClients(clients);
    const rawRedux = extractClients(reduxClients);
    const rawAll = extractClients(apiClientsResponse);
    const rawList = extractClients(listClientsResponse);
    const rawFm = extractClients(fmClientsResponse);

    const merged = [...rawFm, ...rawAll, ...rawList, ...rawProps, ...rawRedux];
    const map = new Map<string, any>();

    merged.forEach((c: any) => {
      if (!c) return;

      const rawAccess = c?.access || c?.lmClient?.access;

      if (rawAccess !== "FM" && rawAccess !== "BOTH") {
        return;
      }

      const name = (
        c.companyName ||
        c.company_name ||
        c.name ||
        c.contactPerson ||
        c.contact_person ||
        c.merchantName ||
        c.clientName ||
        c.title ||
        c.email ||
        c.id ||
        ""
      )
        .toString()
        .trim();

      const idVal = String(c.id || c.clientId || name);

      if (name && !map.has(idVal)) {
        map.set(idVal, {
          ...c,
          displayName: name,
          idValue: idVal,
        });
      }
    });

    return Array.from(map.values());
  }, [
    clients,
    reduxClients,
    apiClientsResponse,
    listClientsResponse,
    fmClientsResponse,
  ]);

  // Helper to extract parent & variant SKUs cleanly from any payload structure
  const extractSkus = (
    items: any[],
  ): Array<{ id: string; name: string; sku: string }> => {
    if (!Array.isArray(items)) return [];
    const list: Array<{ id: string; name: string; sku: string }> = [];

    items.forEach((p: any) => {
      const parentName = (
        p?.itemName ||
        p?.name ||
        p?.title ||
        "Product"
      ).toString();
      const rawSku =
        p?.sku && p?.sku !== "N/A" ? p.sku : p?.productSku || p?.code || "";
      const finalSku = (rawSku || parentName || p?.id || "").toString();

      if (finalSku && finalSku !== "N/A") {
        list.push({
          id: String(p?.id || finalSku),
          name: parentName,
          sku: finalSku,
        });
      }

      // Unpack variant SKUs
      if (Array.isArray(p?.variants) && p.variants.length > 0) {
        p.variants.forEach((v: any) => {
          const vSku = (v?.sku || v?.optionValue || "").toString();
          const vName = v?.optionName
            ? `${parentName} (${v.optionName})`
            : parentName;
          if (vSku && vSku !== "N/A") {
            list.push({
              id: String(v?.id || vSku),
              name: vName,
              sku: vSku,
            });
          }
        });
      }
    });

    return list;
  };

  // Combine products list for selected merchant present in inventory
  const availableProducts = useMemo<any[]>(() => {
    if (!selectedMerchantId) return [];

    const productsSource = Array.isArray(merchantProductsResponse)
      ? merchantProductsResponse
      : Array.isArray(merchantProductsResponse?.data)
        ? merchantProductsResponse.data
        : Array.isArray(merchantProductsResponse?.productItems)
          ? merchantProductsResponse.productItems
          : [];

    const extracted = extractSkus(productsSource);

    const map = new Map<string, { id: string; name: string; sku: string }>();

    extracted.forEach((item) => {
      if (item.sku && !map.has(item.sku)) {
        map.set(item.sku, item);
      }
    });

    return Array.from(map.values());
  }, [selectedMerchantId, merchantProductsResponse]);

  // Check if a merchant is selected
  const isMerchantSelected = Boolean(giftForm.merchantId);

  const [giftList, setGiftList] = useState<Array<{ sku: string; qty: number }>>(
    () => {
      // On edit: populate from all sibling giftItems
      if (giftForm.giftItems && giftForm.giftItems.length > 0) {
        return giftForm.giftItems.map((item: any) => ({
          sku: item.sku || "",
          qty: Number(item.qty) || 1,
        }));
      }
      return [
        { sku: giftForm.giftProductSku || "", qty: giftForm.giftQty || 1 },
      ];
    },
  );

  const todayStr = useMemo(() => new Date().toISOString().split("T")[0], []);

  // Compute available gift products for a specific row index
  const getAvailableGiftProductsForIndex = (currentIdx: number) => {
    if (!isMerchantSelected) return [];
    const otherSelectedSkus = giftList
      .filter((_, i) => i !== currentIdx)
      .map((g) => g.sku)
      .filter(Boolean);

    return availableProducts.filter((p: any) => {
      if (p.sku === giftForm.mainProductSku) return false;
      if (otherSelectedSkus.includes(p.sku)) return false;
      return true;
    });
  };

  const maxGiftsAllowed = Math.max(
    0,
    availableProducts.length - (giftForm.mainProductSku ? 1 : 0),
  );
  const canAddMoreGifts =
    isMerchantSelected &&
    availableProducts.length > 0 &&
    giftList.length < maxGiftsAllowed;

  // Handle single Purchase Trigger SKU change
  const handleTriggerSkuChange = (val: string) => {
    onGiftFormChange({ mainProductSku: val });
    if (giftForm.giftProductSku === val) {
      onGiftFormChange({ giftProductSku: "" });
    }
    setGiftList((prev) =>
      prev.map((g) => (g.sku === val ? { ...g, sku: "" } : g)),
    );
  };

  // Sync gift rows with parent giftForm
  const handleGiftChange = (
    index: number,
    field: "sku" | "qty",
    value: any,
  ) => {
    const updated = [...giftList];
    updated[index] = { ...updated[index], [field]: value };
    setGiftList(updated);
    onGiftFormChange({ giftItems: updated } as any);
    if (index === 0) {
      if (field === "sku") onGiftFormChange({ giftProductSku: value });
      if (field === "qty") onGiftFormChange({ giftQty: Number(value) });
    }
  };

  const addGiftRow = () => {
    if (!canAddMoreGifts) return;
    const updated = [...giftList, { sku: "", qty: 1 }];
    setGiftList(updated);
    onGiftFormChange({ giftItems: updated } as any);
  };

  const removeGiftRow = (index: number) => {
    if (giftList.length <= 1) return;
    const updated = giftList.filter((_, i) => i !== index);
    setGiftList(updated);
    onGiftFormChange({ giftItems: updated } as any);
    if (updated[0]) {
      onGiftFormChange({
        giftProductSku: updated[0].sku,
        giftQty: updated[0].qty,
      });
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Active Gift Rules Table/Cards */}
      <div className="lg:col-span-7 space-y-3">
        <h3 className="text-sm font-medium text-[#212121]">
          Active Gift Rules
        </h3>
        {gifts.length === 0 ? (
          <p className="text-sm text-[#212121]/50 italic">No gift rules.</p>
        ) : (
          gifts.map((g) => {
            const allGiftItems =
              g.giftItems && g.giftItems.length > 0
                ? g.giftItems
                : [{ id: g.id, sku: g.giftProductSku, qty: g.giftQty }];

            return (
              <div
                key={g.id}
                onClick={() => onEditGift && onEditGift(g)}
                className={`bg-white rounded-xl border p-3 shadow-xs transition cursor-pointer hover:border-amber-400 hover:shadow-md ${
                  editingGiftId === g.id
                    ? "border-amber-400 ring-2 ring-amber-400/30 bg-amber-50/20"
                    : "border-slate-200"
                }`}
              >
                <div className="flex justify-between">
                  <span className="font-mono text-sm font-medium text-yellow-700">
                    {g.giftSku}
                  </span>
                  <span className="text-xs text-[#212121]/50">
                    {g.merchant}
                  </span>
                </div>
                <div className="text-xs text-[#212121]/70 mt-1">
                  <div>
                    Buy {g.mainQty}×{" "}
                    <span className="font-mono font-semibold text-slate-700">
                      {g.mainProductSku}
                    </span>
                  </div>
                  {/* Show all complementary gift items */}
                  <div className="mt-1 space-y-0.5">
                    {allGiftItems.map((item: any, idx: number) => (
                      <div
                        key={idx}
                        className="text-emerald-600 flex items-center gap-1"
                      >
                        <span className="text-slate-400 text-[10px]">↳</span>
                        Get {item.qty}×{" "}
                        <span className="font-mono font-semibold">
                          {item.sku}
                        </span>
                        <span className="text-[9px] text-emerald-700 bg-emerald-50 px-1 rounded border border-emerald-100">
                          free
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex justify-between items-center mt-2 text-xs text-[#212121]/50">
                  <span>
                    {g.startDate} → {g.endDate}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      // Delete all sibling records in this campaign
                      const allIds =
                        g.giftItems && g.giftItems.length > 0
                          ? g.giftItems.map((i: any) => i.id).filter(Boolean)
                          : [g.id];
                      allIds.forEach((id: string) => onDeleteGift(id));
                    }}
                    className="text-rose-500 hover:underline cursor-pointer"
                  >
                    Delete
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Gift Assignment Hub Form */}
      <form
        noValidate
        onSubmit={onAddGift}
        className="lg:col-span-5 bg-white rounded-2xl border border-slate-200 shadow-sm p-5 space-y-5"
      >
        {/* Merchant & Gift Campaign SKU */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <NewSelectInput
              name="merchantId"
              label={t ? t("Merchant") : "Merchant"}
              placeholder={t ? t("Select Merchant") : "Select Merchant"}
              value={giftForm.merchantId || ""}
              selectOptions={filteredMerchants.map((c: any) => ({
                id: c.idValue,
                value: c.idValue,
                text: c.displayName,
              }))}
              onChange={(val) => {
                const selected = filteredMerchants.find(
                  (c: any) => c.idValue === val || c.id === val,
                );
                onGiftFormChange({
                  merchantId: val,
                  merchant: selected ? selected.displayName : "",
                  mainProductSku: "",
                  giftProductSku: "",
                });
                setGiftList([{ sku: "", qty: 1 }]);
              }}
            />
          </div>
          <div>
            <NewinputField
              title={t ? t("Gift Campaign SKU") : "Gift Campaign SKU"}
              name="giftSku"
              value={giftForm.giftSku}
              other={{
                onChange: (e: any) =>
                  onGiftFormChange({ giftSku: e.target.value }),
              }}
            />
          </div>
        </div>

        {/* Purchase Trigger & Complimentary Gift */}
        <div className="flex flex-col space-y-4 bg-white p-1 rounded-2xl">
          {/* Purchase Trigger Section */}
          <div className="space-y-1">
            <div className="grid grid-cols-12 gap-2 items-start">
              <div className="col-span-9">
                <NewSelectInput
                  name="mainProductSku"
                  label={t ? t("Purchase Trigger") : "Purchase Trigger"}
                  placeholder={
                    isMerchantSelected
                      ? t
                        ? t("Select Product SKU")
                        : "Select Product SKU"
                      : t
                        ? t("Select Merchant First")
                        : "Select Merchant First"
                  }
                  disabled={!isMerchantSelected}
                  value={giftForm.mainProductSku}
                  selectOptions={
                    isMerchantSelected
                      ? availableProducts.map((p: any) => ({
                          id: p.id || p.sku,
                          value: p.sku,
                          text:
                            p.name && p.name !== p.sku
                              ? `${p.sku} — ${p.name}`
                              : p.sku,
                        }))
                      : []
                  }
                  onChange={(val) => handleTriggerSkuChange(val)}
                />
              </div>
              <div className="col-span-3">
                <NewinputField
                  title={t ? t("Qty") : "Qty"}
                  name="mainQty"
                  type="number"
                  min={1}
                  disabled={!isMerchantSelected}
                  value={giftForm.mainQty}
                  other={{
                    onChange: (e: any) =>
                      onGiftFormChange({
                        mainQty: Math.max(1, Number(e.target.value)),
                      }),
                  }}
                />
              </div>
            </div>
          </div>

          {/* Complimentary Gift Section */}
          <div className="space-y-2 pt-1">
            <div className="flex justify-between items-center">
              <span className="text-xs font-semibold text-[#1E293B]">
                {t ? t("Complimentary Gift *") : "Complimentary Gift *"}
              </span>
              <button
                type="button"
                onClick={addGiftRow}
                disabled={!canAddMoreGifts}
                className={`text-xs font-semibold text-[#B47814] hover:text-amber-800 flex items-center gap-1 transition ${
                  !canAddMoreGifts
                    ? "opacity-50 cursor-not-allowed"
                    : "cursor-pointer"
                }`}
              >
                <IconPlus size={14} /> {t ? t("Add SKU") : "Add SKU"}
              </button>
            </div>

            <div className="space-y-2">
              {giftList.map((item, idx) => (
                <div key={idx} className="grid grid-cols-12 gap-2 items-start">
                  <div
                    className={
                      giftList.length > 1 ? "col-span-8" : "col-span-9"
                    }
                  >
                    <NewSelectInput
                      name={`giftSku_${idx}`}
                      label={t ? t("Gift SKU") : "Gift SKU"}
                      placeholder={
                        isMerchantSelected
                          ? t
                            ? t("Select Gift SKU")
                            : "Select Gift SKU"
                          : t
                            ? t("Select Merchant First")
                            : "Select Merchant First"
                      }
                      disabled={!isMerchantSelected}
                      value={item.sku}
                      selectOptions={
                        isMerchantSelected
                          ? getAvailableGiftProductsForIndex(idx).map(
                              (p: any) => ({
                                id: p.id || p.sku,
                                value: p.sku,
                                text:
                                  p.name && p.name !== p.sku
                                    ? `${p.sku} — ${p.name}`
                                    : p.sku,
                              }),
                            )
                          : []
                      }
                      onChange={(val) => handleGiftChange(idx, "sku", val)}
                    />
                  </div>
                  <div className="col-span-3">
                    <NewinputField
                      title={t ? t("Qty") : "Qty"}
                      name={`giftQty_${idx}`}
                      type="number"
                      min={1}
                      disabled={!isMerchantSelected}
                      value={item.qty}
                      other={{
                        onChange: (e: any) =>
                          handleGiftChange(
                            idx,
                            "qty",
                            Math.max(1, Number(e.target.value)),
                          ),
                      }}
                    />
                  </div>
                  {giftList.length > 1 && (
                    <div className="col-span-1 pt-3 flex items-center justify-center">
                      <button
                        type="button"
                        onClick={() => removeGiftRow(idx)}
                        className="text-slate-400 hover:text-rose-500 transition p-1"
                      >
                        <IconTrash size={16} />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Start Date & End Date */}
        <PremiumDateRangePicker
          title={t ? t("Start & End Date") : "Start & End Date"}
          startDate={giftForm.startDate}
          endDate={giftForm.endDate}
          onChangeRange={({ startDate, endDate }) => {
            onGiftFormChange({ startDate, endDate });
          }}
        />

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 text-sm text-[#212121]/70 border border-slate-200 rounded-xl hover:bg-slate-50 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="px-5 py-2 bg-yellow-100 text-yellow-700 rounded-xl text-sm font-medium hover:bg-yellow-200 transition shadow-sm"
          >
            {editingGiftId ? "Update Gift Rule" : "Deploy Gift Rule"}
          </button>
        </div>
      </form>
    </div>
  );
};
